import { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Input, Select } from 'antd';
import { TypeSearchResult, ISearchResult, TESTAMENTS_TYPES } from 'types';
import { useLanguage } from 'core/hooks';
import { IQueryParams } from 'core/services/bible/chapters.search.service';
import Flex from 'components/common/Flex';
import SearchEmptyState from 'components/common/emptyState/searchEmptyState';

import ResultsGroup from './ResultsGroup';
import ResultsGroupItems from './ResultsGroupItems';

const { Search: AntSearch } = Input;

export enum searchOptions {
  search_all = 'search_all',
  search_old = 'search_old',
  search_new = 'search_new',
  search_book = 'search_book',
}

interface ISearchProps {
  translationId: number;
  searchResult: TypeSearchResult | null;
  searchForWord: (params: { translationId: number; searchedWord: string; queryParams: IQueryParams }) => void;
  onClose: () => void;
  currentBookNumber?: number;
}

export default function Search({
  translationId,
  searchResult,
  searchForWord,
  onClose,
  currentBookNumber,
}: ISearchProps) {
  const navigate = useNavigate();
  const { translations } = useParams();
  const [selectedWord, setSelectedWord] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [queryParams, setQueryParams] = useState<IQueryParams>({});
  const [showWordsList, setShowWordsList] = useState(false);
  const [searchedFirstTime, setSearchedFirstTime] = useState(true);

  const { currentLanguage } = useLanguage();
  const buttons = useMemo(() => [
    { label: currentLanguage.search_all, value: searchOptions.search_all },
    { label: currentLanguage.search_old, value: searchOptions.search_old },
    { label: currentLanguage.search_new, value: searchOptions.search_new },
    { label: currentLanguage.search_book, value: searchOptions.search_book },
  ], [currentLanguage]);

  const getQuery = useCallback((searchOption: searchOptions) => {
    const query: IQueryParams = { bookNumber: '', testament: '' };
    switch (searchOption) {
      case searchOptions.search_book:
        if (currentBookNumber) {
          query.bookNumber = String(currentBookNumber);
        }

        break;
      case searchOptions.search_new:
        query.testament = TESTAMENTS_TYPES.new;
        break;
      case searchOptions.search_old:
        query.testament = TESTAMENTS_TYPES.old;
        break;
      default:
        break;
    }

    return query;
  }, [currentBookNumber]);

  const handleSearch = useCallback(() => {
    searchForWord({ translationId, searchedWord: inputValue, queryParams });
    setShowWordsList(false);
    setSearchedFirstTime(false);
  }, [translationId, inputValue, queryParams, searchForWord]);

  const redirect = (searchedItem: ISearchResult) => {
    const { book, chapter, verseNumber } = searchedItem;
    if (!translations) return;

    navigate(`/bible/${book.number}/${chapter.number}/${verseNumber}/${translations}`, {
      state: { highlight: true },
    });
    onClose();
  };

  const handleOptionChange = useCallback((key: searchOptions) => {
    const query = getQuery(key);
    setQueryParams(query);
    searchForWord({ translationId, searchedWord: inputValue, queryParams: query });
  }, [getQuery, translationId, inputValue, searchForWord]);

  return (
    <div className="search">
      <Flex width="var(--full)">
        <Select
          className="search__options"
          placeholder={currentLanguage.whereToSearch}
          options={buttons}
          onChange={handleOptionChange}
        />
        <AntSearch
          placeholder={currentLanguage.search}
          onChange={({ target }) => setInputValue(target.value)}
          onSearch={handleSearch}
          value={inputValue}
        />
      </Flex>

      <div style={{ paddingRight: 12, maxHeight: '500px', overflowY: 'auto' }}>
        {!searchResult || (!Object.keys(searchResult).length && !searchedFirstTime)
          ? (
            <SearchEmptyState />
          ) : null}
        {!showWordsList && searchResult && Object.keys(searchResult).length > 0 && (
          <ResultsGroup
            searchResult={searchResult}
            setSelectedWord={setSelectedWord}
            setShowWordsList={setShowWordsList}
          />
        )}
        {showWordsList && searchResult && Object.keys(searchResult).length > 0 && (
          <ResultsGroupItems
            results={searchResult}
            selectedKey={selectedWord}
            backToWordsList={setShowWordsList}
            setSearchItem={redirect}
          />
        )}
      </div>
    </div>
  );
}
